import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { authApi } from "../Apis/Api";
import { toast } from "react-toastify";
import logo from "../assets/Group 2085662814.png";

const ForgotPasswordPage = () => {
  const [currentStep, setCurrentStep] = useState('email');
  const [formData, setFormData] = useState({ 
    email: "",
    otp: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({
    isLoading: false,
    message: "",
    type: "",
  });

  const [showPasswords, setShowPasswords] = useState({
    newPassword: false,
    confirmPassword: false
  });
  const [formTouched, setFormTouched] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  
  const location = useLocation();
  const navigate = useNavigate();
  const emailInputRef = useRef(null);
  const otpInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const resendTimeoutRef = useRef(null);

  useEffect(() => {
    if (currentStep === 'email') emailInputRef.current?.focus();
    else if (currentStep === 'otp') otpInputRef.current?.focus();
    else if (currentStep === 'password') passwordInputRef.current?.focus();
  }, [currentStep]);

  useEffect(() => {
    if (resendTimer > 0) {
      resendTimeoutRef.current = setTimeout(() => setResendTimer(prev => prev - 1), 1000);
    }
    return () => clearTimeout(resendTimeoutRef.current);
  }, [resendTimer]);

  const validateForm = () => {
    const newErrors = {};
    if (currentStep === 'email') {
      if (!formData.email.trim()) newErrors.email = "Email is required";
      else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email))
        newErrors.email = "Invalid email address";
    } else if (currentStep === 'otp') {
      if (!formData.otp.trim()) newErrors.otp = "OTP is required";
      else if (!/^\d{6}$/.test(formData.otp)) newErrors.otp = "OTP must be 6 digits";
    } else if (currentStep === 'password') {
      if (!formData.newPassword.trim()) newErrors.newPassword = "New password is required";
      else if (formData.newPassword.length < 6) newErrors.newPassword = "Password must be at least 6 characters";
      if (!formData.confirmPassword.trim()) newErrors.confirmPassword = "Please confirm your password";
      else if (formData.newPassword !== formData.confirmPassword)
        newErrors.confirmPassword = "Passwords don't match";
    }
    setErrors(newErrors);
    return !Object.keys(newErrors).length;
  };

  useEffect(() => { if (formTouched) validateForm(); }, [formData, formTouched, currentStep]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (!formTouched) setFormTouched(true);
  };

  const showToast = (msg, type = "error") => {
    type === "success" ? toast.success(msg) : toast.error(msg);
  };

  const handleSendOtp = async () => {
    setFormTouched(true);
    if (!validateForm()) return;
    setStatus({ ...status, isLoading: true });
    try {
      await authApi.sendOtp(formData.email);
      showToast("OTP sent successfully!", "success");
      setCurrentStep('otp');
      setResendTimer(60);
      setFormData(prev => ({ ...prev, otp: "" }));
    } catch (err) {
      showToast(err?.message || "Failed to send OTP");
    } finally {
      setStatus({ ...status, isLoading: false });
    }
  };

  const handleVerifyOtp = async () => {
    setFormTouched(true);
    if (!validateForm()) return;
    setStatus({ ...status, isLoading: true });
    try {
      await authApi.verifyOtp(formData.otp);
      showToast("OTP verified!", "success");
      setCurrentStep('password');
    } catch (err) {
      showToast(err?.message || "Invalid OTP");
    } finally {
      setStatus({ ...status, isLoading: false });
    }
  };

  const handleResetPassword = async () => {
    setFormTouched(true);
    if (!validateForm()) return;
    setStatus({ ...status, isLoading: true });
    try {
      await authApi.resetPassword(formData.email, formData.newPassword);
      showToast("Password reset successfully!", "success");
      setTimeout(() => navigate("/sign-in", { state: { message: "Password changed! Please sign in.", type: "success" } }), 2000);
    } catch (err) {
      showToast(err?.message || "Failed to reset password");
    } finally {
      setStatus({ ...status, isLoading: false });
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    setStatus({ ...status, isLoading: true });
    try {
      await authApi.sendOtp(formData.email);
      showToast("OTP resent!", "success");
      setResendTimer(60);
    } catch (err) {
      showToast(err?.message || "Failed to resend OTP");
    } finally {
      setStatus({ ...status, isLoading: false });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (currentStep === 'email') handleSendOtp();
    else if (currentStep === 'otp') handleVerifyOtp();
    else if (currentStep === 'password') handleResetPassword();
  };

  return (
    <>
      <style>
        {`
          * { margin: 0; padding: 0; box-sizing: border-box; }

          .auth-container {
            min-height: 100vh;
            background: black;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 1rem;
            position: relative;
            overflow: hidden;
          }

          .auth-container::before {
            content: '';
            position: absolute;
            top: 0; left: 0;
            width: 100%;
            height: 60%;
            background: linear-gradient(to bottom, rgba(0,0,0,0.9), rgba(0,0,0,0.7), transparent);
            z-index: 1;
            pointer-events: none;
          }

          .background-video {
            position: absolute;
            top: 0; left: 0;
            width: 100%; height: 100%;
            object-fit: cover;
            z-index: 0;
          }

          .auth-logo {
  position: absolute;
  top: 1.5rem;
  left: 1.5rem;
  z-index: 10;
  background-color: white;
  padding: 0.5rem;
  border-radius: 0.5rem;
}

.auth-logo img {
  height: 30px;
  width: auto;
  object-fit: contain;
}

          .auth-section {
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 1rem;
            overflow: hidden;
            // box-shadow: 0 20px 40px rgba(0,0,0,0.3);
            max-width: 500px;
            width: 100%;
            min-height: 380px;
            position: relative;
            z-index: 2;
          }

          .auth-right {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            padding: 2rem;
            background: white;
            overflow-y: auto;
            border-radius: 1rem;
          }

          .auth-form {
            width: 100%;
            max-width: 38rem;
            background: white;
          }

          .auth-form h3 {
            font-size: 1.5rem;
            font-weight: 700;
            color: #1a202c;
            margin: 0 0 0.25rem;
            text-align: center;
          }

          .auth-form > p {
            color: #718096;
            margin: 0 0 1.5rem;
            text-align: center;
            font-size: 1rem;
          }

          .form-group { position: relative; margin-bottom: 1rem; }
          label { display: block; font-size: 0.8rem; color: #4a5568; margin-bottom: 0.35rem; font-weight: 500; }
          input {
            width: 100%;
            padding: 0.8rem 1rem 0.8rem 3rem;
            border: 1px solid #e2e8f0;
            border-radius: 0.5rem;
            background: #f7fafc;
            transition: all 0.3s;
            font-size: 0.95rem;
          }
          input:focus { outline: none; border-color: #E63C23; background: white; box-shadow: 0 0 0 3px rgba(230,60,35,0.1); }
          input.is-invalid { border-color: #e53e3e; }

          .icon {
            position: absolute;
            left: 0.9rem;
            top: 50%;
            transform: translateY(-50%);
            color: #a0aec0;
            font-size: 1.2rem;
            pointer-events: none;
            z-index: 2;
          }

          .password-toggle {
            position: absolute;
            right: 0.9rem;
            top: 50%;
            transform: translateY(-50%);
            color: #a0aec0;
            cursor: pointer;
            font-size: 1.2rem;
            z-index: 5;
          }
          .password-toggle:hover { color: #E63C23; }

          .password-hint {
            font-size: 0.7rem;
            color: #e53e3e;
            margin-top: 0.35rem;
          }

          button[type="submit"], .social-btn {
            width: 100%;
            padding: 0.75rem;
            background: #E63C23;
            color: white;
            border: none;
            border-radius: 0.5rem;
            font-weight: 600;
            margin-top: 1rem;
            cursor: pointer;
          }
          button[type="submit"]:disabled { background: #cbd5e0; cursor: not-allowed; }

          .back-link {
            text-align: center;
            margin-top: 1rem;
            font-size: 0.8rem;
            color: #718096;
          }
          .back-link a { color: #E63C23; text-decoration: none; font-weight: 600; }
          .back-link a:hover { text-decoration: underline; }

          .resend-otp { text-align: center; margin-top: 0.5rem; }
          .resend-otp button {
            background: none;
            border: none;
            color: #E63C23;
            text-decoration: underline;
            cursor: pointer;
            padding: 0.25rem;
            margin-top: 0.25rem;
          }
          .resend-otp button:disabled { color: #999; cursor: not-allowed; text-decoration: none; }

          /* Responsive Design - Tablet */
          @media (max-width: 1024px) {
            .auth-section { max-width: 450px; }
            .auth-form h3 { font-size: 1.35rem; }
          }

          /* Responsive Design - Mobile */
          @media (max-width: 768px) {
            .auth-container { padding: 0.5rem; }
            .auth-logo { top: 1rem; left: 1rem; }
            .auth-logo img { height: 40px; }
            
            .auth-section { 
              max-width: 95%; 
              min-height: auto;
            }
            
            .auth-right { padding: 1.5rem 1.25rem; }
            
            .auth-form h3 { font-size: 1.25rem; }
            .auth-form > p { font-size: 0.9rem; margin-bottom: 1.25rem; }
            
            label { font-size: 0.75rem; }
            input { padding: 0.7rem 0.9rem 0.7rem 2.75rem; font-size: 0.9rem; }
            
            .icon { left: 0.8rem; font-size: 1.1rem; }
            .password-toggle { right: 0.8rem; font-size: 1.1rem; }
            
            button[type="submit"] { padding: 0.7rem; font-size: 0.9rem; }
          }

          /* Responsive Design - Small Mobile */
          @media (max-width: 480px) {
            .auth-logo { top: 0.75rem; left: 0.75rem; }
            .auth-logo img { height: 35px; }
            
            .auth-section { 
              max-width: 100%; 
              border-radius: 0.75rem;
            }
            
            .auth-right { padding: 1.25rem 1rem; }
            
            .auth-form h3 { font-size: 1.15rem; }
            .auth-form > p { font-size: 0.85rem; margin-bottom: 1rem; }
            
            label { font-size: 0.7rem; margin-bottom: 0.3rem; }
            input { 
              padding: 0.65rem 0.8rem 0.65rem 2.5rem; 
              font-size: 0.85rem;
              border-radius: 0.4rem;
            }
            
            .icon { left: 0.7rem; font-size: 1rem; }
            .password-toggle { right: 0.7rem; font-size: 1rem; }
            
            .password-hint { font-size: 0.65rem; }
            
            button[type="submit"] { 
              padding: 0.65rem; 
              font-size: 0.85rem;
              margin-top: 0.75rem;
            }
            
            .back-link { margin-top: 0.75rem; font-size: 0.75rem; }
            .resend-otp { font-size: 0.8rem; }
          }

          /* Responsive Design - Extra Small Mobile */
          @media (max-width: 360px) {
            .auth-right { padding: 1rem 0.75rem; }
            .auth-form h3 { font-size: 1.05rem; }
            .auth-form > p { font-size: 0.8rem; }
            input { padding: 0.6rem 0.7rem 0.6rem 2.3rem; font-size: 0.8rem; }
            .icon { left: 0.6rem; font-size: 0.95rem; }
            .password-toggle { right: 0.6rem; font-size: 0.95rem; }
          }
        `}
      </style>

      <div className="auth-container">
        <div className="auth-logo">
          <img src={logo} alt="Logo" />
        </div>

        <video className="background-video" src="assets/video/auth-video-v2.mp4" autoPlay  muted />

        <section className="auth-section">
          <div className="auth-right">
            <div className="auth-form">
              <p className="fw-bolder display-5 h7">
                {currentStep === 'email' ? 'Forgot Password' : 
                 currentStep === 'otp' ? 'Verify OTP' : 'Set New Password'}
              </p>
              <p>
                {currentStep === 'email' && 'Enter your email to receive an OTP'}
                {currentStep === 'otp' && 'Check your email for the 6-digit code'}
                {currentStep === 'password' && 'Choose a strong new password'}
              </p>

              <form onSubmit={handleSubmit}>
                {currentStep === 'email' && (
                  <div className="form-group">
                    <label>Email</label>
                    <div style={{position: 'relative'}}>
                      <span className="icon"><Icon icon="mage:email" /></span>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter your email"
                        ref={emailInputRef}
                        className={errors.email ? "is-invalid" : ""}
                      />
                    </div>
                    {errors.email && <p className="password-hint">{errors.email}</p>}
                  </div>
                )}

                {currentStep === 'otp' && (
                  <div className="form-group">
                    <label>Enter OTP</label>
                    <div style={{position: 'relative'}}>
                      <span className="icon"><Icon icon="mdi:key-variant" /></span>
                      <input
                        type="text"
                        name="otp"
                        value={formData.otp}
                        onChange={handleChange}
                        placeholder="6-digit code"
                        maxLength="6"
                        ref={otpInputRef}
                        className={errors.otp ? "is-invalid" : ""}
                      />
                    </div>
                    {errors.otp && <p className="password-hint">{errors.otp}</p>}
                    <div className="resend-otp">
                      <button type="button" onClick={handleResendOtp} disabled={resendTimer > 0}>
                        {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                      </button>
                    </div>
                  </div>
                )}

                {currentStep === 'password' && (
                  <>
                    <div className="form-group">
                      <label>New Password</label>
                      <div style={{position: 'relative'}}>
                        <span className="icon"><Icon icon="solar:lock-password-outline" /></span>
                        <input
                          type={showPasswords.newPassword ? "text" : "password"}
                          name="newPassword"
                          value={formData.newPassword}
                          onChange={handleChange}
                          placeholder="Enter new password"
                          ref={passwordInputRef}
                          className={errors.newPassword ? "is-invalid" : ""}
                        />
                        <span className="password-toggle" onClick={() => setShowPasswords(p => ({...p, newPassword: !p.newPassword}))}>
                          <Icon icon={showPasswords.newPassword ? "ri:eye-off-line" : "ri:eye-line"} />
                        </span>
                      </div>
                      {errors.newPassword && <p className="password-hint">{errors.newPassword}</p>}
                    </div>

                    <div className="form-group">
                      <label>Confirm Password</label>
                      <div style={{position: 'relative'}}>
                        <span className="icon"><Icon icon="solar:lock-password-outline" /></span>
                        <input
                          type={showPasswords.confirmPassword ? "text" : "password"}
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          placeholder="Confirm new password"
                          className={errors.confirmPassword ? "is-invalid" : ""}
                        />
                        <span className="password-toggle" onClick={() => setShowPasswords(p => ({...p, confirmPassword: !p.confirmPassword}))}>
                          <Icon icon={showPasswords.confirmPassword ? "ri:eye-off-line" : "ri:eye-line"} />
                        </span>
                      </div>
                      {errors.confirmPassword && <p className="password-hint">{errors.confirmPassword}</p>}
                    </div>
                  </>
                )}

                <button className="social-btn d-flex align-items-center justify-content-center gap-2" type="submit" disabled={status.isLoading}>
                  {status.isLoading ? "Please wait..." : 
                    currentStep === 'email' ? "Send OTP" :
                    currentStep === 'otp' ? "Verify OTP" : "Reset Password"
                  }
                </button>

                <p className="back-link">
                  <Link to="/sign-in">← Back to Sign In</Link>
                </p>
              </form>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ForgotPasswordPage;